'use client';

import {
  ISalePageDetailResponseProduct,
  ISalePageProductAdditionalInformResponse,
} from '@/api/types';
import { Button } from '@/components';
import { Dropdown } from '@/components/dropdown';
import { qaAttr, qaId } from '@/lib/qa';
import { Column, Element, Icon, Row } from '@vuddy/components';
import { useState } from 'react';
import { PurchaseItemContent } from '../common/PurchaseItemContent';
import * as styles from './digitalProductSelectDropdown.css';

type DigitalProduct = ISalePageDetailResponseProduct &
  ISalePageProductAdditionalInformResponse;

interface DigitalProductSelectDropdownProps {
  itemList: DigitalProduct[];
  selectedIds: number[];
  onToggle: (itemId: number) => void;
  onCodeInputClick?: (itemId: number) => void;
  verifiedProductCodes?: Map<number, string>;
}

export const DigitalProductSelectDropdown = ({
  itemList,
  selectedIds,
  onToggle,
  onCodeInputClick,
  verifiedProductCodes,
}: DigitalProductSelectDropdownProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Dropdown
      open={open}
      onChangeOpen={setOpen}
      hasParentWidth
      options={itemList.map(item => ({
        ...item,
        label: item.name,
      }))}
      triggerClassName={styles.trigger}
      triggerProps={qaAttr(qaId('sale.purchase', 'product-selector.trigger'))}
      contentClassName={styles.content}
      placement="bottom-start"
      offset={8}
      enableSizeMiddleware={true}
      closeOnOptionClick={false}
      onMenuClick={id => onToggle(Number(id))}
      renderOption={(item: DigitalProduct) => {
        const checked = selectedIds.includes(item.id);
        const isPurchaseLimited =
          !item.isPurchasable && item.remainingPurchaseCount === 0;
        const disabled = item.saleStatus !== 'ON_SALE' || isPurchaseLimited;

        return (
          <Row
            alignItems="flex-start"
            gap="gap_xl"
            className={styles.optionItem}
            aria-disabled={disabled ? 'true' : undefined}
            data-disabled={disabled ? 'true' : undefined}>
            <Icon
              name="FILL_CHECK_CIRCLE"
              size="24px"
              color={
                disabled ? 'disabled' : checked ? 'vuddyPrimary' : 'disabled'
              }
            />
            <PurchaseItemContent
              item={item}
              hasVerificationCode={item.requiresPromotionCode}
              verificationCodeName={verifiedProductCodes?.get(item.id)}
              onCodeInputClick={() => onCodeInputClick?.(item.id)}
            />
          </Row>
        );
      }}
      footer={setDropdownOpen => (
        <Element className={styles.footer}>
          <Button
            variant="solidSecondary"
            size="medium"
            fullWidth
            onClick={() => setDropdownOpen(false)}>
            선택 완료
          </Button>
        </Element>
      )}>
      <Row width="100%" onClick={() => setOpen(!open)}>
        <Element flex={1} typography="bodyBase" color="placeholder">
          상품을 선택해 주세요.
        </Element>
        <Icon
          name={open ? 'FILL_TRIANGLE_UP' : 'FILL_TRIANGLE_DOWN'}
          size="16px"
          color="placeholder"
        />
      </Row>
    </Dropdown>
  );
};

interface SelectedDigitalProductListProps {
  items: DigitalProduct[];
  onDelete: (itemId: number) => void;
  onCodeInputClick?: (itemId: number) => void;
  verifiedProductCodes?: Map<number, string>;
}

export const SelectedDigitalProductList = ({
  items,
  onDelete,
  onCodeInputClick,
  verifiedProductCodes,
}: SelectedDigitalProductListProps) => {
  if (items.length === 0) return null;

  return (
    <Column className={styles.selectedList}>
      {items.map(item => (
        <Column key={item.id} className={styles.selectedItem}>
          <Row alignItems="flex-start" justifyContent="space-between">
            <Column style={{ gap: '2px' }}>
              <Element typography="bodyXsAccent" color="secondary">
                {item.name}
              </Element>
              <Element typography="bodyXs" color="tertiary">
                {item.description}
              </Element>
              {item.requiresPromotionCode && (
                <Row
                  as="button"
                  alignItems="center"
                  gap="gap_2xs"
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    onCodeInputClick?.(item.id);
                  }}
                  className={styles.closeButton}>
                  <Icon
                    name="FILL_PROMOTION_CODE"
                    size="16px"
                    color="secondary"
                  />
                  <Element typography="captionBaseAccent" color="secondary">
                    {verifiedProductCodes?.get(item.id) || '코드 입력'}
                  </Element>
                </Row>
              )}
            </Column>
            <button
              type="button"
              aria-label={`${item.name} 삭제`}
              onClick={() => onDelete(item.id)}
              className={styles.closeButton}>
              <Icon name="OUTLINE_CLOSE" size="20px" color="tertiary" />
            </button>
          </Row>
          <Element
            typography="bodyBaseAccent"
            color="primary"
            textAlign="right">
            {item.price.amount.toLocaleString()}원
          </Element>
        </Column>
      ))}
    </Column>
  );
};
